import { useEffect, useRef, useState } from "react";

export type CalloutAlignment = "left" | "right" | "xs-right";
export type CalloutArrowPosition = "left" | "right" | "center" | "xs-center";

export interface MobileFullWidthPanelPosition {
    offsetPx: number;
    widthPx: number;
    arrowOffsetPx: number;
}

interface DynamicPositionResult {
    align: CalloutAlignment;
    arrowPosition: CalloutArrowPosition;
    maxWidthPx: number | null;
    mobileFullWidth: MobileFullWidthPanelPosition | null;
}

interface TriggerRect {
    left: number;
    width: number;
}

const calloutMaxWidthPx = 512;
const narrowViewportPx = 768;
const viewportMarginPx = 16;
const minCalloutWidthPx = 240;
const arrowEdgeInsetPx = 24;

const maxWidthForAvailableSpace = (availablePx: number): number =>
    Math.max(minCalloutWidthPx, Math.min(calloutMaxWidthPx, availablePx));

const mobileFullWidthPosition = (
    trigger: TriggerRect,
    viewportWidth: number,
): MobileFullWidthPanelPosition => {
    const widthPx = viewportWidth - viewportMarginPx * 2;
    const offsetPx = viewportMarginPx - trigger.left;
    const triggerCenterPx = trigger.left + trigger.width / 2;
    const arrowOffsetPx = Math.min(
        Math.max(triggerCenterPx - viewportMarginPx, arrowEdgeInsetPx),
        widthPx - arrowEdgeInsetPx,
    );
    return { offsetPx, widthPx, arrowOffsetPx };
};

export const calloutPositionForTrigger = (
    trigger: TriggerRect,
    viewportWidth: number,
): DynamicPositionResult => {
    const triggerRight = trigger.left + trigger.width;

    if (viewportWidth < narrowViewportPx) {
        const triggerCenter = trigger.left + trigger.width / 2;
        const isInLeftHalf = triggerCenter < viewportWidth / 2;
        return {
            align: isInLeftHalf ? "left" : "xs-right",
            arrowPosition: isInLeftHalf ? "left" : "xs-center",
            maxWidthPx: null,
            mobileFullWidth: mobileFullWidthPosition(trigger, viewportWidth),
        };
    }
    if (trigger.left + calloutMaxWidthPx > viewportWidth) {
        return {
            align: "right",
            arrowPosition: "right",
            maxWidthPx: maxWidthForAvailableSpace(triggerRight - viewportMarginPx),
            mobileFullWidth: null,
        };
    }
    return {
        align: "left",
        arrowPosition: "left",
        maxWidthPx: maxWidthForAvailableSpace(
            viewportWidth - trigger.left - viewportMarginPx,
        ),
        mobileFullWidth: null,
    };
};

export const useDynamicCalloutPosition = (): [
    React.RefObject<HTMLDivElement>,
    DynamicPositionResult,
] => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<DynamicPositionResult>({
        align: "left",
        arrowPosition: "left",
        maxWidthPx: calloutMaxWidthPx,
        mobileFullWidth: null,
    });

    useEffect(() => {
        const updatePosition = () => {
            if (!elementRef.current) return;

            const rect = elementRef.current.getBoundingClientRect();
            setPosition(
                calloutPositionForTrigger(
                    { left: rect.left, width: rect.width },
                    window.innerWidth,
                ),
            );
        };

        updatePosition();

        const resizeObserver = new ResizeObserver(updatePosition);
        const mutationObserver = new MutationObserver(updatePosition);

        if (elementRef.current) {
            resizeObserver.observe(elementRef.current);
            mutationObserver.observe(elementRef.current.parentNode || document.body, {
                childList: true,
                subtree: true,
            });
        }

        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
        };
    }, []);

    return [elementRef, position];
};
